import cn from '@integrations/classnames'
import type * as React from '@integrations/react'
import type { ReactNode } from '@integrations/react'
import { useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, PaymentOptionResponse } from 'mmfintech-backend-api/types'
import { checkSingleValue, isValidFunction, isValidString } from 'mmfintech-commons'
import styles from './InputAmount.module.scss'
import InputAmountLimits from './InputAmountLimits'

interface Props {
  label?: ReactNode
  amount?: string
  account?: AccountBalanceResponse
  setAmount?: (value: string) => void
  paymentOption?: PaymentOptionResponse
  error?: string
  setAmountError?: (error: string) => void
  placeholder?: string
  dataTest?: string
  autoFocus?: boolean
  required?: boolean
  bordered?: boolean
  [key: string]: any
}

function InputAmount({
  label,
  amount,
  account,
  setAmount,
  paymentOption,
  error,
  setAmountError,
  placeholder,
  dataTest,
  autoFocus,
  ...rest
}: Props) {
  const { currencyCode } = account || {}

  const { getCurrencyPrecision } = useCurrencies()

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (isValidFunction(setAmount)) {
      const val = checkSingleValue(e.target.value, {
        validation: 'float',
        decimals: getCurrencyPrecision(currencyCode)
      })
      setAmount(val)
      setAmountError?.(null)
    }
  }

  return (
    <div className={cn(styles['input-amount-wrapper'], 'input-amount-wrapper')} {...rest}>
      <label className={cn(styles['input-amount-label'], 'input-amount-label')}>{label}</label>

      {isValidFunction(setAmount) ? (
        <input
          className={cn(styles['input-amount-element'], 'input-amount-element')}
          type='text'
          value={amount || ''}
          onChange={handleAmountChange}
          placeholder={placeholder || '0'}
          maxLength={16}
          data-test={dataTest}
          autoFocus={autoFocus}
        />
      ) : (
        <div className={cn(styles['input-amount-static'], 'input-amount-static')} data-test={dataTest}>
          {amount || 0}
        </div>
      )}

      {isValidString(error) && <div className={cn(styles['input-amount-error'], 'input-amount-error')}>{error}</div>}
      <InputAmountLimits currency={currencyCode} paymentOption={paymentOption} />
    </div>
  )
}

export default InputAmount
